/**
 * <easysearch-lookup> — registration-number parts finder (progressive enhancement).
 *
 * On submit it asks ValerydFitment (assets/fitment.js) — the one module in front of the
 * EasySearch App Proxy, which owns the endpoint, the country fallback, the timeout and the
 * session cache — and gets back normalised JSON: { found, resultCode, trailer, articleLists,
 * messages }. Each articleNumber IS the product SKU (Valeryd article number = Shopify SKU), so
 * we ask window.ValerydCards.cards() (assets/product-resolver.js) for their cards — the SAME
 * server-rendered product card the normal search uses — so a reg-nr search looks and behaves
 * like an ordinary results page (just scoped to parts that fit the trailer).
 *
 * Article numbers with no matching live product (discontinued / removed SKUs) are dropped —
 * we never render a dead link or a bare SKU.
 *
 * The result is split the way the current site splits it: **by catalogue category**, the
 * top-level category and the level below it, each group named by the more specific of the
 * two (renderResults). That grouping is not the backend's — measured 2026-09-07, a reg-nr
 * lookup comes back as ONE `articleLists` entry (`searchTypeKey: "regno"`) with every article
 * in it, so `docs/easysearch-backend-delta.md`
 * §1's ask is still open. It does not need to be answered: each card the server renders
 * carries its own place in the tree (`data-section` / `data-group` / `data-order`), so the
 * category comes from Shopify's own collections. When a query DOES match several search
 * types at once (brake-ID and OE, say) those lists stay the outer grouping and the
 * categories nest inside them.
 *
 * All of that is for a TRAILER. A part identifier — a brake ID, an OE or article number, a
 * brake dimension — names no trailer, and its parts go to the store's own results page
 * instead (ValerydCards.searchUrl), with the filters, sort and paging any search has
 * (Åke 2026-09-29). Only a list past that page's 250-product ceiling stays here.
 *
 * The theme never sees SOAP, credentials or ranking — that lives in a separate middleware.
 * HTML-first: the form works without JS; this only adds the live lookup. Built with DOM
 * nodes + textContent (no innerHTML) so external data can't inject markup.
 */
if (!customElements.get('easysearch-lookup')) {
  // Search types worth naming in a result heading (e.g. "Broms-ID: 20-2425/1"). Reg-nr is
  // covered by the trailer line, and free-text (arttext) needs no heading.
  const TITLED_TYPES = ['bromsid', 'oe', 'artno', 'brakedimmanuf'];
  customElements.define(
    'easysearch-lookup',
    class EasySearchLookup extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('[data-easysearch-form]');
        this.results = this.querySelector('[data-easysearch-results]');
        this.standard = document.querySelector('[data-easysearch-standard]');
        if (!this.form || !this.results) return;
        this.onSubmit = (event) => {
          event.preventDefault();
          this.lookup();
        };
        this.form.addEventListener('submit', this.onSubmit);

        // Auto mode (search results page): the header search lands here as ?q=…. Run the lookup
        // for ANY non-empty query — EasySearch auto-detects the type. renderResults() only takes
        // over the page (hiding the standard product grid) when it gets back an *identifier* match
        // (reg-nr / brake-id / OE / article no / dimension); a free-text query that only matched
        // part names — or matched nothing — stays silent, so the normal product results remain.
        const auto = (this.dataset.autoQuery || '').trim();
        if (auto) {
          this.auto = true;
          this.lookup();
        }
      }

      disconnectedCallback() {
        if (this.form) this.form.removeEventListener('submit', this.onSubmit);
        if (this.lookupController) this.lookupController.abort();
      }

      status(message) {
        // No role here: the results container is already aria-live="polite", and
        // nesting role="alert"/"status" inside it double-announces (assertive-in-polite).
        const p = document.createElement('p');
        p.className = 'easysearch__status';
        p.textContent = message;
        this.results.replaceChildren(p);
      }

      setResultsBusy(busy) {
        if (!this.results) return;
        this.results.setAttribute('aria-busy', busy ? 'true' : 'false');
      }

      standardResults() {
        if (this.standard) return this.standard;
        if (typeof document.querySelector !== 'function') return null;
        this.standard = document.querySelector('[data-easysearch-standard]');
        return this.standard;
      }

      setStandardHidden(hidden) {
        const standard = this.standardResults();
        if (standard) standard.hidden = hidden;
      }

      // A miss/error: in auto mode stay silent (clear) so the normal product results below
      // remain the fallback; in manual mode show the message.
      done(message) {
        this.setResultsBusy(false);
        if (this.auto) {
          this.results.replaceChildren();
          this.setStandardHidden(false);
          return;
        }
        this.status(message);
      }

      fail(message) {
        this.setResultsBusy(false);
        if (this.auto && this.dataset.standardEmpty !== 'true') return this.done();
        this.setStandardHidden(true);
        this.status(message);
      }

      async lookup() {
        const input = this.form.querySelector('[name="regnr"]');
        // The registry to query. It also decides the language of the labels the backend
        // returns, which is why the theme never renders those — see listTitle(). The field is
        // rendered with SE, the default that resolves; regnr-country.js swaps in the visitor's
        // country. ValerydFitment applies the same fallback when the field is missing.
        const countryEl = this.form.querySelector('[name="country"]');
        const regnr = ((input && input.value) || '').trim();
        const lookupCountry = (countryEl && countryEl.value) || '';
        if (!regnr) {
          if (input) input.focus();
          return;
        }
        if (this.lookupController) this.lookupController.abort();
        const controller = new AbortController();
        this.lookupController = controller;
        const standardEmpty = this.dataset.standardEmpty === 'true';
        if (this.auto && standardEmpty) this.setStandardHidden(true);
        this.setResultsBusy(true);
        if (!this.auto || standardEmpty) this.status(this.dataset.searching || 'Searching…');

        try {
          // The ceiling on the proxy call lives in ValerydFitment — deliberately not on the
          // whole lookup: resolving the returned SKUs to product cards legitimately takes ~1.3s
          // for a large fitment list, so a timeout spanning renderResults would abort searches
          // that are working. A timeout routes to the service-error state, which the theme
          // distinguishes from a genuine zero-result (PR #112) — silence would be the one wrong
          // answer, since it tells the customer nothing.
          const result = await window.ValerydFitment.lookup(regnr, { country: lookupCountry, signal: controller.signal });
          if (result.kind === 'aborted' || controller.signal.aborted) return; // a newer query replaced this one
          this.track(regnr, lookupCountry, result);
          if (!result.ok) {
            if (result.kind === 'invalid') return this.done(this.dataset.invalid || this.dataset.error);
            if (result.kind === 'error') return this.fail(this.dataset.error || 'Lookup failed.');
            return this.fail(this.dataset.unavailable || this.dataset.error || 'Lookup failed.');
          }
          const data = result.data;

          // A trailer in the response means a reg-nr matched — persist it (assets/
          // trailer-context.js) so the header chip and the product-page "fits your
          // trailer" rail can reuse the fitment context across visits. Save before
          // resolving product cards so a large fitment list cannot delay the switch.
          if (data && data.found !== false && data.trailer && window.ValerydTrailer) {
            window.ValerydTrailer.save(regnr, (document.documentElement && document.documentElement.dataset.regnrCountry) || '', data);
          }
          await this.renderResults(data, regnr, controller.signal);
        } finally {
          if (this.lookupController === controller) {
            this.lookupController = null;
            this.setResultsBusy(false);
          }
        }
      }

      async renderResults(data, regnr, signal) {
        if (signal && signal.aborted) return;
        const groups = this.articleGroups(data);
        const total = groups.reduce((n, g) => n + g.articleNumbers.length, 0);
        if (!data || data.found === false || total === 0) {
          const msg = (data && Array.isArray(data.messages) && data.messages[0]) || this.dataset.noResults;
          return this.done(msg);
        }

        // Auto mode only takes over the results page for an identifier match — a free-text query
        // that merely matched part names falls through to the standard product results below.
        if (this.auto && !this.hasIdentifierMatch(data)) return this.done();

        // A part identifier names no trailer: its parts get the store's own results page.
        // Replaced on the results page, so Back cannot land on a page that sends the shopper on
        // again; a click on a brake ID is a step they can go back from.
        if (!data.trailer && this.hasIdentifierMatch(data)) {
          const url = window.ValerydCards.searchUrl(window.ValerydFitment.skus(data), regnr);
          if (url) {
            if (this.auto) window.location.replace(url);
            else window.location.assign(url);
            return;
          }
        }

        // Once EasySearch has identified the query, replace the generic loader with useful
        // trailer context and card skeletons while the matching SKUs resolve through Shopify.
        this.showLoadingResults(data, groups, regnr);
        if (this.auto) this.setStandardHidden(true);

        // Resolve SKUs to live product cards in the server's order. Articles with no live
        // product are dropped entirely — the backend now filters those out and floats
        // in-stock ones first, so this order is meaningful rather than the raw ascending
        // article numbers it used to be.
        //
        // The cap is a safety net against a pathological response, not a relevance filter.
        // It was 48, which silently cost real results: a reg-nr search returns up to 267
        // articles, so two thirds of the catalogue never got looked up, and because the list
        // was ascending the cut always fell on the same high-numbered series.
        let budget = 300;
        const capped = groups
          .map((group) => {
            const skus = group.articleNumbers.slice(0, Math.max(budget, 0));
            budget -= skus.length;
            return { title: group.title, skus: skus };
          })
          .filter((group) => group.skus.length);
        const wanted = capped.reduce((all, group) => all.concat(group.skus), []);
        let rendered;
        try {
          rendered = await window.ValerydCards.cards(wanted, { signal: signal });
        } catch (error) {
          if (signal && signal.aborted) return;
          return this.fail(this.dataset.unavailable || this.dataset.error);
        }
        if (signal && signal.aborted) return;
        const resolved = capped
          .map((group) => ({
            title: group.title,
            cards: group.skus.map((sku) => rendered.bySku.get(sku.trim().toUpperCase())).filter(Boolean),
          }))
          .filter((group) => group.cards.length);

        if (!resolved.length) {
          if (rendered.incomplete) return this.fail(this.dataset.unavailable || this.dataset.error);
          // A trailer matched but none of its parts exist in the catalogue.
          return this.done(this.dataset.noResults);
        }

        // Every card is in hand and each one names its collections, so the saved trailer gets
        // its article-number → collections index here and nowhere else. A category page then
        // answers "how many of this trailer's parts are in me" with no request at all
        // (assets/trailer-filter.js).
        if (data && data.trailer && window.ValerydTrailer) {
          const categories = {};
          rendered.bySku.forEach((li, sku) => {
            const handles = (li.dataset.collections || '')
              .split(',')
              .map((handle) => handle.trim())
              .filter(Boolean);
            if (handles.length) categories[sku] = handles;
          });
          window.ValerydTrailer.saveCategories(categories);
        }

        const frag = document.createDocumentFragment();

        this.appendResultContext(frag, data, resolved, regnr);

        resolved.forEach((group) => {
          // Per-group heading only when there's more than one group (a single typed match is
          // already named by the h2 above, so a second heading would just repeat it).
          const named = Boolean(group.title) && resolved.length > 1;
          if (named) {
            frag.appendChild(this.heading(3, group.title, 'easysearch__group'));
          }
          // Inside each search-type list, split by where the parts live in the catalogue: one
          // group per sub-category, the groups in their top-level category's order. Each group
          // is named once, by the most specific category it has — the sub-category, or the
          // top-level category for parts that sit directly on it (the service kits). "Tail
          // light" needs no "Lighting" over it (Åke 2026-09-29).
          const categories = [];
          this.categorySections(group.cards).forEach((section) => {
            section.subs.forEach((sub) => categories.push({ title: sub.title || section.title, cards: sub.cards }));
          });
          // Every level below is derived from the last heading actually rendered above these
          // categories — the type heading when there is one, otherwise the page's own h2. A
          // heading that is skipped because it would name everything must not leave a hole in
          // the outline behind it, so nothing here is a fixed number.
          const above = named ? 3 : 2;
          // ONE grid for the whole list, so a group opens in the cell after the last one ends.
          // A grid per group left a ragged row behind each: FED164's 61 parts come in 23 groups,
          // 15 of them a single card — 84 empty cells at five columns (measured 2026-09-29). The
          // heading that opens a group rides on its first card (.easysearch__label), so the
          // outline a screen reader walks is still heading → its cards.
          const grid = document.createElement('ul');
          grid.className = 'easysearch__grid';
          grid.setAttribute('role', 'list');
          categories.forEach((category) => {
            const categoryNamed = Boolean(category.title) && categories.length > 1;
            // One below the deepest heading actually rendered above these cards.
            const cardLevel = above + (categoryNamed ? 2 : 1);
            // Cloned, so a SKU listed under two search types renders a card in each — and so
            // the same card can carry a different heading level in each.
            category.cards.forEach((li, index) => {
              const item = window.ValerydCards.atLevel(li.cloneNode(true), cardLevel);
              if (categoryNamed && index === 0) {
                const label = document.createElement('div');
                label.className = 'easysearch__label';
                label.appendChild(this.heading(above + 1, category.title, 'easysearch__group'));
                item.prepend(label);
              }
              grid.appendChild(item);
            });
          });
          frag.appendChild(grid);
        });

        if (signal && signal.aborted) return;
        this.results.replaceChildren(frag);
        this.setResultsBusy(false);
        this.updateDocumentTitle(data, regnr);

        // Auto mode found fitment parts — hide the standard product results (the reg-nr won't
        // match products by title anyway), so the fitment grid stands as the results page.
        if (this.auto) {
          this.setStandardHidden(true);
        }
      }

      updateDocumentTitle(data, regnr) {
        if (!this.auto || !data || !data.trailer) return;
        const template = (this.dataset.pageTitle || '').trim();
        if (!template || typeof document.title !== 'string') return;
        document.title = template.replace('%REGNR%', regnr);
      }

      appendResultContext(frag, data, groups, regnr) {
        const heading = document.createElement('h2');
        heading.className = 'easysearch__results-heading';
        heading.textContent = this.resultsHeading(groups, regnr);
        frag.appendChild(heading);

        const trailerLine = this.trailerLine(data.trailer);
        if (trailerLine) frag.appendChild(trailerLine);

        const trailerSpecs = this.trailerSpecs(data.trailer);
        if (trailerSpecs) frag.appendChild(trailerSpecs);

        const brakeIds = this.brakeIdLine(data.trailer);
        if (brakeIds) frag.appendChild(brakeIds);
      }

      showLoadingResults(data, groups, regnr) {
        const frag = document.createDocumentFragment();
        this.appendResultContext(frag, data, groups, regnr);

        const grid = document.createElement('ul');
        grid.className = 'easysearch__grid easysearch__grid--loading';
        grid.setAttribute('aria-hidden', 'true');
        for (let index = 0; index < 4; index += 1) {
          const item = document.createElement('li');
          const skeleton = document.createElement('div');
          skeleton.className = 'easysearch__skeleton';
          item.appendChild(skeleton);
          grid.appendChild(item);
        }
        frag.appendChild(grid);

        this.setResultsBusy(true);
        this.results.replaceChildren(frag);
      }

      heading(level, text, className) {
        const h = document.createElement('h' + level);
        h.className = className;
        h.textContent = text;
        return h;
      }

      // Split a list of cards into catalogue sections — the shape the current site gives a
      // reg-nr result: one section per top-level category ("Axles & wheel brakes"), split by
      // the level below it ("Wheel brakes", "Drums & bearings"). 57 fitting parts for one
      // trailer measured 7 sections; grouping by the deepest category instead gave 39 groups,
      // most of them a single card, which is a list of headings rather than a page.
      //
      // Every value is read off the card the server rendered (sections/product-card-grid.liquid
      // → snippets/category-path.liquid): titles, and `data-order` for the section's place in
      // the megamenu. Nothing about the catalogue's shape is decided here.
      //
      // Cards with no category at all land in one trailing bucket, and the ORDER WITHIN a
      // group is left exactly as the server sent it — the backend floats in-stock articles
      // first, and re-sorting here would throw that away.
      categorySections(cards) {
        const sections = new Map();
        cards.forEach((li) => {
          const title = (li.dataset.section || '').trim();
          const parsed = Number.parseInt(li.dataset.order, 10);
          const key = title || ' other';
          if (!sections.has(key)) {
            sections.set(key, {
              // An uncategorised card sorts behind even the categories the menu does not
              // carry (99), so the bucket cannot land between two real sections.
              title: title || (this.dataset.groupOther || '').trim(),
              order: title ? (Number.isFinite(parsed) ? parsed : 99) : 100,
              subs: new Map(),
            });
          }
          const section = sections.get(key);
          const sub = (li.dataset.group || '').trim();
          if (!section.subs.has(sub)) section.subs.set(sub, { title: sub, cards: [] });
          section.subs.get(sub).cards.push(li);
        });
        // sort() is stable, so sections and sub-groups that tie keep the server's order.
        return Array.from(sections.values())
          .sort((a, b) => a.order - b.order)
          .map((section) => ({
            title: section.title,
            // The cards that sit directly on the top-level category go FIRST: named by the
            // top-level category itself, they are the section's general parts, ahead of its
            // sub-categories rather than wherever the backend happened to send them.
            subs: Array.from(section.subs.values()).sort((a, b) => (a.title ? 1 : 0) - (b.title ? 1 : 0)),
          }));
      }

      // Group results by the backend's search-type lists; only the types worth a heading get one
      // (reg-nr/VIN are named by the trailer line, free text needs none).
      articleGroups(data) {
        return window.ValerydFitment.groups(data).map((group) => ({
          title: TITLED_TYPES.indexOf(group.searchTypeKey) !== -1 ? this.listTitle(group) : '',
          articleNumbers: group.articleNumbers,
        }));
      }

      // "{type}: {term}", e.g. "Brake ID: 20-2425/1".
      //
      // The type name comes from OUR translations, keyed on the backend's
      // language-independent searchTypeKey — never from its searchTypeText. The
      // backend translates that field according to the `country` parameter, and that
      // same parameter also decides which registry answers: asking for English labels
      // (`EN`) returns no trailer at all for a Swedish plate. So the storefront has to
      // send a country that resolves and translate the labels itself, or it would show
      // "Broms-ID" on an English page — or, worse, trade the lookup away for a label.
      listTitle(group) {
        const key = group.searchTypeKey;
        const translated = key && this.dataset['type' + key.charAt(0).toUpperCase() + key.slice(1)];
        const type = translated || group.searchTypeText;
        const term = group.searchFor;
        if (type && term) return type + ': ' + term;
        return type || term || '';
      }

      // The h2: a single titled (non-reg-nr) match names itself; everything else uses the template.
      resultsHeading(groups, regnr) {
        const titled = groups.filter((g) => g.title);
        if (groups.length === 1 && titled.length === 1) return titled[0].title;
        return (this.dataset.resultsHeading || '').replace('%REGNR%', regnr);
      }

      // One `fitment_lookup` per lookup for GTM (docs/analytics-tracking.md): a trailer (reg-no or
      // VIN) or a part identifier, found or not. Free text is left to the store's own search
      // event, and the query never leaves: a registration number can name a person.
      track(query, country, result) {
        if (!window.dataLayer) return;
        const fitment = window.ValerydFitment;
        const data = result.ok ? result.data : null;
        const trailer = Boolean(data && data.found !== false && data.trailer);
        const part = !trailer && Boolean(data && this.hasIdentifierMatch(data));
        const type = trailer || fitment.looksLikeIdentifier(query) ? 'trailer' : part || fitment.looksLikePartIdentifier(query) ? 'part' : '';
        if (!type) return;
        const count = trailer || part ? fitment.skus(data).length : 0;
        window.dataLayer.push({
          event: 'fitment_lookup',
          lookup_type: type,
          lookup_result: result.ok ? (count ? 'found' : 'not_found') : result.kind === 'invalid' ? 'not_found' : 'error',
          part_count: count,
          lookup_country: country,
        });
      }

      // True when EasySearch matched a structured identifier (not just a free-text part name) —
      // gates the auto-mode page takeover so normal product searches aren't hijacked.
      hasIdentifierMatch(data) {
        return window.ValerydFitment.hasIdentifierMatch(data);
      }

      // The brakes fitting this trailer (FoundTrailer.BrakeIDs) as buttons that re-run the lookup
      // for that brake-id — so a shopper can jump from "my trailer" to "all parts for this brake".
      brakeIdLine(trailer) {
        const ids = trailer && Array.isArray(trailer.brakeIds) ? trailer.brakeIds.filter(Boolean) : [];
        if (!ids.length) return null;
        const wrap = document.createElement('p');
        wrap.className = 'easysearch__brakes';
        const label = this.dataset.brakesLabel;
        if (label) {
          const span = document.createElement('span');
          span.className = 'easysearch__brakes-label';
          span.textContent = label;
          wrap.appendChild(span);
        }
        ids.forEach((id) => {
          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'easysearch__brake-id';
          btn.textContent = id;
          btn.addEventListener('click', () => this.searchFor(id));
          wrap.appendChild(btn);
        });
        return wrap;
      }

      // Fill the field and look up a term as a manual search (e.g. from a brake-id button).
      searchFor(term) {
        const input = this.form.querySelector('[name="regnr"]');
        if (input) input.value = term;
        this.auto = false; // a deliberate click always shows its result
        this.lookup();
      }

      trailerLine(trailer) {
        const text = window.ValerydFitment.describe(trailer, { braked: this.dataset.braked, unbraked: this.dataset.unbraked });
        if (!text) return null;
        const p = document.createElement('p');
        p.className = 'easysearch__trailer u-mono';
        p.textContent = text;
        return p;
      }

      // Labelled spec grid for the matched trailer — weights (kg) and dimensions (mm) the
      // backend passes through from FoundTrailer. A field is skipped when EasySearch omits it
      // or returns '0' (height is frequently 0). Labels are localised via data-* attributes;
      // the kg/mm units are SI and stay in code.
      trailerSpecs(trailer) {
        if (!trailer) return null;
        const fields = [
          ['totalWeight', this.dataset.specTotalWeight, 'kg'],
          ['serviceWeight', this.dataset.specServiceWeight, 'kg'],
          ['loadWeight', this.dataset.specLoadWeight, 'kg'],
          ['width', this.dataset.specWidth, 'mm'],
          ['length', this.dataset.specLength, 'mm'],
          ['height', this.dataset.specHeight, 'mm'],
        ];
        const dl = document.createElement('dl');
        dl.className = 'easysearch__specs';
        fields.forEach(([key, label, unit]) => {
          const value = ((trailer[key] == null ? '' : trailer[key]) + '').trim();
          if (!label || !value || value === '0') return;
          const cell = document.createElement('div');
          cell.className = 'easysearch__spec';
          const dt = document.createElement('dt');
          dt.className = 'easysearch__spec-label';
          dt.textContent = label;
          const dd = document.createElement('dd');
          dd.className = 'easysearch__spec-value';
          dd.textContent = value + ' ' + unit;
          cell.appendChild(dt);
          cell.appendChild(dd);
          dl.appendChild(cell);
        });
        return dl.children.length ? dl : null;
      }
    }
  );
}
